import { Button, Flex, Form, Modal, Radio, Switch, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useState } from 'react';

import { IconClose } from '@assets';
import { ensureFreshToken, getBearerToken } from '@auth';
import { Select } from '@components';
import { statusesList, typesList } from '@constants';
import { RangePicker } from '@entities';
import { useCurrentCompanyId } from '@hooks';
import { captureException } from '@sentry/react';
import { downloadZip, getDateDefault, setSearchParams } from '@utils';
import { env } from '@utils/env';

import * as S from './styled';

type DownloadDocumentsModalProps = {
  page: number;
  open: boolean;
  onCancel: () => void;
  defaultValues?: {
    type?: string;
    status?: string;
    withErrors?: boolean;
    period?: Dayjs[];
    filterDateBy?: string;
  };
  onRefreshList?: () => void;
};

const downloadFilterOptions = [
  { label: 'By upload date', value: 'createdAt' },
  { label: 'By issue date', value: 'issueDate' },
];

const DownloadDocumentsModal: FC<DownloadDocumentsModalProps> = ({
  page,
  open,
  onCancel,
  defaultValues,
  onRefreshList,
}) => {
  const companyId = useCurrentCompanyId();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (
    values: NonNullable<DownloadDocumentsModalProps['defaultValues']>
  ) => {
    const params = {
      ...(page && { page }),
      ...(values.type && { type: values.type }),
      ...(values.status && { status: values.status }),
      // The error facet, as the list has it: a filter that combines with the status rather than
      // replacing it. Without this the export could only be narrowed by status, so the one thing an
      // operator exports a list for — the flagged documents — had to be filtered by eye afterwards.
      ...(values.withErrors && { withErrors: true }),
      ...(values.filterDateBy && { filterDateBy: values.filterDateBy }),
      ...(values.period?.length && {
        dateTo: dayjs(values.period[1]).format('YYYY-MM-DD'),
        dateFrom: dayjs(values.period[0]).format('YYYY-MM-DD'),
      }),
    };

    setIsLoading(true);

    try {
      // This download goes out on a raw fetch rather than through the axios client, so it has to
      // refresh and read the token the same way the interceptor does.
      await ensureFreshToken();

      await downloadZip({
        url: `${env.VITE_ROUTE_API_BASE_URL}/api/admin/documents/bulk_download?${setSearchParams(params)}`,
        headers: {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getBearerToken()}`,
            CompanyId: companyId!,
          },
        },
        fileName: 'documents',
      });

      onRefreshList?.();
      onCancel();
    } catch (err) {
      captureException(err);
      message.error('Download files error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(
    () => {
      if (defaultValues && Object.keys(defaultValues)?.length) {
        Object.keys(defaultValues)?.forEach((key) =>
          // @ts-expect-error-next-line
          form.setFieldValue(key, defaultValues[key])
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValues]
  );

  return (
    <Modal
      width={630}
      title={
        <Flex gap={9} vertical>
          <S.Title>Download documents</S.Title>
          <S.Description>
            By clicking the button you’ll download all documents matching
            filters
          </S.Description>
        </Flex>
      }
      open={open}
      destroyOnClose
      onCancel={onCancel}
      footer={null}
      closable={!isLoading}
      centered
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <S.Block gap={20}>
          <Form.Item label="Period" name="period">
            <RangePicker
              key="transaction-date"
              variant="outlined"
              value={[
                form.getFieldValue('dateFrom'),
                form.getFieldValue('dateTo'),
              ]}
              // @ts-expect-error-next-line
              onChange={(date: Date[] | null) => {
                form.setFieldValue(
                  'dateFrom',
                  date === null ? '' : getDateDefault(date[0], 'YYYY-MM-DD')
                );
                form.setFieldValue(
                  'dateTo',
                  date === null ? '' : getDateDefault(date[1], 'YYYY-MM-DD')
                );
              }}
            />
          </Form.Item>
        </S.Block>
        <S.Block gap={20}>
          <Form.Item label="Filter By" name="filterDateBy">
            <Radio.Group
              options={downloadFilterOptions}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
        </S.Block>

        <S.Block gap={20}>
          <Form.Item label="Type" name="type">
            <Select
              variant="outlined"
              placeholder="Select type"
              options={typesList}
              size="large"
              clearIcon={<IconClose width={16} height={16} />}
              onClear={() => form.resetFields(['type'])}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Status" name="status">
            <Select
              variant="outlined"
              placeholder="Select status"
              options={statusesList}
              size="large"
              clearIcon={<IconClose width={16} height={16} />}
              onClear={() => form.resetFields(['status'])}
              allowClear
            />
          </Form.Item>
        </S.Block>

        <S.Block gap={20}>
          <Form.Item
            label="With errors"
            name="withErrors"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </S.Block>

        <Flex justify="flex-end">
          <S.Footer>
            <Button size="large" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isLoading}
            >
              Download
            </Button>
          </S.Footer>
        </Flex>
      </Form>
    </Modal>
  );
};

export default DownloadDocumentsModal;
